import {
  NAV_LINK_BG,
  NAV_LINK_ICON,
  NAV_LINK_SELECTED_ICON,
  NAV_LINK_SELECTED_TEXT,
  NAV_LINK_TEXT,
  NAV_LINK_TINT_BG,
} from "@funded-here-interface/common/src/constant/color";
import { createStyles } from "@mantine/emotion";

export const useStyles = createStyles(() => ({
  // Outer wrapper around <nav>. Needs its own explicit size: <nav> is
  // position: fixed (escapes normal flow), so without a height this div
  // collapses to 0 and its background never paints — same class of bug as
  // InvestorLayout's mainContainer.
  wrapper: {
    width: 240,
    height: "100vh",
  },
  // Embedded (mobile Drawer) counterpart to `wrapper` — this div is
  // ALWAYS rendered (NavBar.tsx renders it unconditionally around <nav>),
  // and it's what's actually the flex item inside drawerBody's flex column,
  // not <nav> itself. `.embedded` used to put `flex: 1` on <nav>, but a
  // flex property only does anything when the element's own PARENT is the
  // flex container — <nav>'s parent is THIS div, so that flex:1 was a
  // no-op: <nav> just sized to its content instead of filling the drawer,
  // which is why its flex:1-positioned footer sat well short of the
  // drawer's actual bottom edge instead of pinned to it.
  embeddedWrapper: {
    flex: 1,
    minHeight: 0,
  },
  sideBarContainer: {
    position: "fixed",
    height: "100vh",
    backgroundColor: "#FFFFFF",
    width: 240,
    display: "flex",
    flexDirection: "column",
    fontFamily: "Poppins",
    // Left corners only: the sidebar sits flush against `hero` on the right
    // with no gap and no matching curve there, so rounding that side cut a
    // notch into the grey background instead of a clean edge. Left corners
    // face the open page edge, where rounding actually reads as an edge.
    borderRadius: "0px 24px 24px 0px",
    // Without this the rounded corners are invisible: the sidebar and the
    // page area behind it (InvestorLayout's mainContainer) are both #FFFFFF,
    // so the curve just reveals more white with nothing to contrast against.
    border: "1px solid #edf0f2",
    borderRight: "none",
    boxShadow: "0 4px 14px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)",
    // border-radius alone doesn't clip children — without this, content near
    // the edge (e.g. the active nav item's mint highlight pill) can render
    // past the rounded corner instead of being cut off by it.
    overflow: "hidden",
  },
  // Inside the mobile Drawer, the drawer's own content wrapper already
  // supplies the rounded corners + shadow (InvestorLayout's drawerContent) —
  // repeating them here would double up as a border-within-a-border.
  //
  // `height: "100%"` (not `flex: 1` — see `embeddedWrapper` above for why):
  // `embeddedWrapper` is the actual flex item that claims the drawer's
  // remaining height after its 44px header, so <nav> just needs to fill
  // that already-correctly-sized parent.
  embedded: {
    position: "static",
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
  },
  // Left edge lines up with the nav icons below: scrollArea's 12px left
  // margin + NavLink's own 12px (--mantine-spacing-sm) left padding = 24px
  // from the sidebar edge.
  iconbox: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "32px 20px 32px 30px",
  },
  // Mobile drawer only — the drawer's own 44px close-button header already
  // sits above this, so the same 32px top padding as the desktop sidebar
  // (which has no such header) stacked into an oversized gap before the
  // logo. Left/right/bottom stay the same so the logo's horizontal position
  // and the space below it match the desktop sidebar.
  iconboxEmbedded: {
    paddingTop: 12,
  },
  logo: {
    width: "70%",
  },
  link: {
    fontSize: 14,
    textDecoration: "none",
    color: NAV_LINK_TEXT,
    backgroundColor: NAV_LINK_BG,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    // Explicit so hover and active tints paint the same-width pill —
    // Mantine's own NavLink base styles don't guarantee width:100% here,
    // which let the two states' backgrounds visibly differ in size.
    width: "100%",
    boxSizing: "border-box",
    marginBottom: 6,

    "& .mantine-NavLink-label": {
      color: NAV_LINK_TEXT,
    },
    // Mantine 9's NavLink shares one "section" class for both leftSection
    // (the icon) and rightSection (a submenu chevron) — "icon" is not a
    // real selector — so the two are told apart only by data-position.
    "& .mantine-NavLink-section[data-position='left']": {
      color: NAV_LINK_ICON,
    },

    // Hover only tints the background — icon/text stay the resting slate
    // colour, per the design spec (hover column matches non-selected).
    "&:hover:hover": {
      backgroundColor: NAV_LINK_TINT_BG,
    },

    "&[data-active][data-active]": {
      backgroundColor: NAV_LINK_TINT_BG,
      "& .mantine-NavLink-label": {
        color: NAV_LINK_SELECTED_TEXT,
        fontWeight: 600,
      },
      "& .mantine-NavLink-section": {
        color: NAV_LINK_SELECTED_ICON,
      },
    },
  },
  scrollArea: {
    flex: 1,
    margin: "0 20px 0 12px",
  },
  // Mirrors Mantine's NavLink box metrics — `8px var(--mantine-spacing-sm)` with
  // the same `spacing-sm` gap after the left section — so these identity rows
  // and the Logout NavLink below them share one icon and label baseline.
  personItem: {
    display: "flex",
    alignItems: "center",
    padding: "8px var(--mantine-spacing-sm)",
    gap: "var(--mantine-spacing-sm)",
    color: NAV_LINK_TEXT,
  },
  // flexShrink: 0 stops the icon from being squeezed narrower than its
  // declared size when the sibling Text wraps to multiple lines (the org
  // name row) — without it, that row's icon renders visibly smaller and its
  // gap narrower than the single-line rows above/below, throwing off the
  // shared left edge all three row labels are meant to share.
  personIcon: {
    color: NAV_LINK_ICON,
    flexShrink: 0,
  },
  // scrollArea's flex: 1 already pushes this to the bottom of the sidebar
  // regardless of how few nav items there are — margin-bottom is what
  // controls how close it actually sits to that bottom edge, so it's kept
  // small (unlike the top margin, which just separates it from the nav
  // list above).
  footer: {
    margin: "24px 20px 16px 12px",
    color: NAV_LINK_TEXT,
  },
}));
